import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  PreviewOrder,
  LoadingSpinner,
  LoadingSpinnerContainer,
  ErrorBox,
  Button,
  ButtonType,
} from '@amzn/burberry-ui-components';
import type { PreviewOrderShipment } from '@amzn/burberry-ui-components';
import { awsRum } from 'src/rum';
import { AppDispatch } from 'src/store/store';
import { getStage } from 'src/utils/roadieHelper';
import {
  selectCart,
  getPreviewOrder,
  getConfirmOrder,
  resetPreviewOrderState,
  selectPreviewOrderInProgress,
  selectPreviewOrderComplete,
  selectConfirmOrderInProgress,
  selectConfirmOrderComplete,
  selectOrder,
  selectOrderError,
} from 'src/store/cartSlice';
import {
  hydrateCatalog,
  selectCatalog,
  selectHydrateCatalogComplete,
  selectCatalogError,
} from 'src/store/catalogSlice';
import {
  selectCsrfToken,
  selectClientId,
  selectExpiryTime,
  selectValidateTokenComplete,
  selectValidateTokenInProgress,
  getValidateToken,
  getClientId,
} from 'src/store/authSlice';
import { Paths } from 'src/paths/Paths';
import { metricEmitter } from 'src/utils/metricEmitter';
import { handleAuth, reloadWindowOnStorageChange, getOrderErrorMessage, getOrderErrorLink } from 'src/utils/utils';
import { getRedirectUrlForCurrentStage } from 'src/constants/lwa';
import {
  EMPTY_CART_STRING,
  EMPTY_CART_ERROR_STRING,
  TAKE_ME_TO_CART_STRING,
  SHIPPING_TO_STRING,
  ENDING_IN_STRING,
  SHIPMENT_STRING,
  UH_OH,
  getPriceNumberFromString,
  priceToUSDStringFormatter,
  getPaymentMethodString,
  findCatalogItemUsingPurchaseItem,
  getChargeByType,
} from '@amzn/d2c-application-website-common';
import './Order.scss';

const PAGE_NAME = 'Order';

const Order: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const cart = useSelector(selectCart);
  const catalog = useSelector(selectCatalog);
  const hydrateCatalogComplete = useSelector(selectHydrateCatalogComplete);
  const catalogError = useSelector(selectCatalogError);
  const csrfToken = useSelector(selectCsrfToken);
  const clientId = useSelector(selectClientId);
  const expiryTime = useSelector(selectExpiryTime);
  const accessTokenExists = useSelector(selectValidateTokenComplete);
  const validTokenInProgress = useSelector(selectValidateTokenInProgress);
  const order = useSelector(selectOrder);
  const orderError = useSelector(selectOrderError);
  const previewOrderInProgress = useSelector(selectPreviewOrderInProgress);
  const previewOrderComplete = useSelector(selectPreviewOrderComplete);
  const confirmOrderInProgress = useSelector(selectConfirmOrderInProgress);
  const confirmOrderComplete = useSelector(selectConfirmOrderComplete);

  const isNotEmpty = cart?.items && cart.items.length > 0;
  const cartAsins = cart?.items?.map((i: any) => i.asin).join(',') ?? '';
  const pageLoadTime = useRef(Date.now());
  const pageLoadEmitted = useRef(false);
  const [hasAttemptedAuth, setHasAttemptedAuth] = useState(false);

  // Page view metric
  useEffect(() => {
    awsRum?.recordEvent('PageView', { page: PAGE_NAME });
  }, []);

  // Reset preview order state on mount
  useEffect(() => {
    dispatch(resetPreviewOrderState());
  }, []);

  // Validate token and fetch client ID on mount
  useEffect(() => {
    dispatch(getValidateToken({ stage: getStage(), csrfToken }));
    dispatch(getClientId({ stage: getStage() }));
  }, []);

  // Listen for auth state changes from LWA popup
  useEffect(() => {
    const authState = { expiryTime: expiryTime ?? null, clientId, csrfToken, isAuthInProgress: false };
    return reloadWindowOnStorageChange(dispatch, authState);
  }, [dispatch, expiryTime, clientId, csrfToken]);

  // Redirect to success page on confirm
  useEffect(() => {
    if (confirmOrderComplete) {
      awsRum?.recordEvent('OrderConfirmed', { purchaseId: confirmOrderComplete });
      navigate(`${Paths.successCheckout}?confirmOrderComplete=${confirmOrderComplete}`);
    }
  }, [confirmOrderComplete]);

  // Hydrate catalog from cart items
  useEffect(() => {
    if (isNotEmpty) {
      const identifiers = cart.items.map((item: any) => ({ itemAsin: item.asin }));
      dispatch(hydrateCatalog({
        itemIdentifiers: identifiers,
        stage: getStage(),
        locale: 'en-US',
        csrfToken,
      }));
    }
  }, [dispatch, cartAsins, csrfToken]);

  // Once catalog is hydrated: check auth, then preview order
  useEffect(() => {
    if (hydrateCatalogComplete && isNotEmpty && !validTokenInProgress) {
      const isExpired = expiryTime ? Date.now() > expiryTime : true;
      if (isExpired) {
        handleAuth(clientId || '', getRedirectUrlForCurrentStage());
      } else {
        const purchaseItems = cart.items.map((item: any) => ({
          asin: item.asin,
          offerId: item.offerId || '',
          merchantId: item.merchantId || '',
          quantity: item.quantity,
        }));
        dispatch(getPreviewOrder({
          stage: getStage(),
          marketplaceId: 'US',
          locale: 'en-US',
          purchaseItems,
          csrfToken,
        }));
      }
    }
  }, [hydrateCatalogComplete, accessTokenExists, validTokenInProgress, expiryTime, csrfToken]);

  // If catalog hydration fails with auth error, trigger re-auth (once)
  useEffect(() => {
    if (catalogError && !hasAttemptedAuth) {
      const status = (catalogError as any)?.status;
      if (status === 401 || status === 403) {
        setHasAttemptedAuth(true);
        handleAuth(clientId || '', getRedirectUrlForCurrentStage());
      }
    }
  }, [catalogError]);

  // Page load metric
  useEffect(() => {
    if (!pageLoadEmitted.current && order) {
      pageLoadEmitted.current = true;
      const loadTime = Date.now() - pageLoadTime.current;
      awsRum?.recordEvent('PageLoad', { page: PAGE_NAME, loadTimeMs: String(loadTime) });
    }
  }, [order]);

  const handleConfirmOrder = () => {
    awsRum?.recordEvent('ButtonClick', { action: 'ConfirmOrder' });
    const isExpired = expiryTime ? Date.now() > expiryTime : true;
    if (isExpired) {
      handleAuth(clientId || '', getRedirectUrlForCurrentStage());
    } else if (previewOrderComplete) {
      dispatch(getConfirmOrder({
        stage: getStage(),
        purchaseId: previewOrderComplete,
        csrfToken,
      }));
    }
  };

  // Map order data to PreviewOrder shipments
  const buildShipments = (): PreviewOrderShipment[] => {
    if (!order?.orders || !catalog) return [];
    const orders = order.orders;
    return orders.map((shipment: any, idx: number) => ({
      label: orders.length > 1 ? `${SHIPMENT_STRING} ${idx + 1}` : `${SHIPMENT_STRING} 1`,
      items: (shipment.items || []).map((orderItem: any) => {
        const catalogItem = findCatalogItemUsingPurchaseItem(catalog, { asin: orderItem.asin });
        const img = catalogItem?.images?.[0];
        const imageUrl = (img as any)?.highRes?.url || (img as any)?.lowRes?.url || '';
        return {
          image: imageUrl,
          title: orderItem.title || catalogItem?.name || '',
          variations: catalogItem?.variations?.dimensions?.map((dim: any) => ({
            key: dim.name || '',
            value: dim.values?.[0] || '',
          })),
          arrivalDate: orderItem.estimatedArrivalDate
            ? new Date(Number(orderItem.estimatedArrivalDate) * 1000).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
            : undefined,
          priceEach: catalogItem?.price || '',
          quantity: orderItem.quantity || 1,
          subtotal: catalogItem?.price
            ? priceToUSDStringFormatter.format(getPriceNumberFromString(catalogItem.price) * (orderItem.quantity || 1))
            : '',
        };
      }),
    }));
  };


  // Loading state
  if (previewOrderInProgress || confirmOrderInProgress || (!hydrateCatalogComplete && isNotEmpty)) {
    return (
      <div className="order-page order-page--loading" data-testid="order-page">
        <LoadingSpinnerContainer>
          <LoadingSpinner />
        </LoadingSpinnerContainer>
      </div>
    );
  }

  // Error state
  if (orderError) {
    const errorCodes = orderError as string[];
    const errorMessage = getOrderErrorMessage(errorCodes);
    const errorLink = getOrderErrorLink(errorCodes);
    return (
      <div className="order-page order-page--error" data-testid="order-page">
        <ErrorBox
          heading={UH_OH}
          message={errorMessage}
          metricEmitter={metricEmitter}
        />
        {errorLink && (
          <a
            href={errorLink.url}
            target="_blank"
            rel="noreferrer"
            className="order-page__error-link"
            data-testid="order-error-link"
          >
            {errorLink.text}
          </a>
        )}
        <Button
          type={ButtonType.DEFAULT}
          text={TAKE_ME_TO_CART_STRING}
          onClick={() => navigate(Paths.cart)}
          metricEmitter={metricEmitter}
        />
      </div>
    );
  }

  // Empty cart
  if (!isNotEmpty) {
    return (
      <div className="order-page order-page--empty" data-testid="order-page">
        <ErrorBox
          heading={EMPTY_CART_STRING}
          message={EMPTY_CART_ERROR_STRING}
          metricEmitter={metricEmitter}
        />
        <Button
          type={ButtonType.CONTINUE_SHOPPING}
          onClick={() => navigate(Paths.home)}
          metricEmitter={metricEmitter}
        />
      </div>
    );
  }

  // Order preview ready — render PreviewOrder component
  if (order) {
    const shipments = buildShipments();
    return (
      <div className="order-page" data-testid="order-page">
        <PreviewOrder
          shipments={shipments}
          subtotal={getChargeByType(order?.charges, 'ITEMS_SUBTOTAL') || priceToUSDStringFormatter.format(cart.subtotal || 0)}
          discounts={getChargeByType(order?.charges, 'ITEMS_DISCOUNT_TOTAL')}
          shippingFee={getChargeByType(order?.charges, 'SHIPPING_FEE_TOTAL')}
          estimatedTax={getChargeByType(order?.charges, 'ESTIMATED_TAX_TOTAL')}
          total={getChargeByType(order?.charges, 'PURCHASE_TOTAL') || priceToUSDStringFormatter.format(cart.subtotal || 0)}
          shipTo={order.shippingAddress?.zipCode ? `${SHIPPING_TO_STRING} ${order.shippingAddress.zipCode}` : undefined}
          paymentMethod={order.paymentMethods?.[0]?.tail ? `${getPaymentMethodString(order.paymentMethods[0].type || '')}${ENDING_IN_STRING}${order.paymentMethods[0].tail}` : undefined}
          onPlaceOrder={handleConfirmOrder}
          metricEmitter={metricEmitter}
        />
      </div>
    );
  }

  // Waiting for order data (auth in progress or preview pending)
  return (
    <div className="order-page order-page--loading" data-testid="order-page">
      <LoadingSpinnerContainer>
        <LoadingSpinner />
      </LoadingSpinnerContainer>
    </div>
  );
};

export default Order;
